import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import useIsMobile from '../hooks/useIsMobile';

const SCAN_FORMATS = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
];

export default function CodeScannerDialog({
  open,
  onScan,
  onClose,
  title = 'Scan serial number',
  subtitle = '',
  scanDelayMs = 1500,
}) {
  const isMobile = useIsMobile();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const lastScanRef = useRef({ text: '', at: 0 });
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const [error, setError] = useState('');
  const [starting, setStarting] = useState(true);
  // This session's scans, newest first: { text, image }
  const [scans, setScans] = useState([]);

  // Short confirmation beep via the Web Audio API (no asset file needed).
  const playBeep = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880; // A5 — short, pleasant
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    } catch (_) {
      /* audio is best-effort */
    }
  }, []);

  const vibrate = useCallback(() => {
    try {
      if (navigator.vibrate) navigator.vibrate(120);
    } catch (_) {
      /* vibration is best-effort */
    }
  }, []);

  // Grab the current video frame as a compact JPEG data URL (reference only).
  const captureFrame = useCallback(() => {
    try {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return null;
      const maxW = 320; // thumbnail scale — keep memory small, never uploaded
      const scale = Math.min(1, maxW / video.videoWidth);
      const w = Math.round(video.videoWidth * scale);
      const h = Math.round(video.videoHeight * scale);
      const canvas = canvasRef.current || document.createElement('canvas');
      canvasRef.current = canvas;
      canvas.width = w;
      canvas.height = h;
      const cx = canvas.getContext('2d');
      cx.drawImage(video, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', 0.6);
    } catch (_) {
      return null;
    }
  }, []);

  const stop = useCallback(() => {
    try {
      if (controlsRef.current) {
        controlsRef.current.stop();
        controlsRef.current = null;
      }
    } catch (_) {
      /* ignore */
    }
    readerRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setError('');
    setStarting(true);
    setScans([]);
    lastScanRef.current = { text: '', at: 0 };

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, SCAN_FORMATS);
    hints.set(DecodeHintType.TRY_HARDER, true);

    const reader = new BrowserMultiFormatReader(hints);
    readerRef.current = reader;

    const start = async () => {
      // Camera APIs require a secure context (https:// or localhost).
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera not available. Open the app over HTTPS or on localhost.');
        setStarting(false);
        return;
      }

      try {
        // Prefer the rear ("environment") camera and request high resolution.
        // Thin 1D barcode bars (Code 128) need sharp frames; a low-res stream
        // often can't resolve them even though QR codes still decode fine.
        const constraints = {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        };

        controlsRef.current = await reader.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result) => {
            if (cancelled || !result) return;
            const text = (result.getText() || '').trim();
            if (!text) return;
            const now = Date.now();
            // Debounce repeats of the same value within scanDelayMs.
            if (
              text === lastScanRef.current.text &&
              now - lastScanRef.current.at < scanDelayMs
            ) {
              return;
            }
            lastScanRef.current = { text, at: now };

            const image = captureFrame();
            playBeep();
            vibrate();

            // Commit immediately to the parent (serial + reference image).
            onScanRef.current(text, image);

            // Keep an in-dialog record (newest first), de-duped by value.
            setScans((prev) =>
              prev.some((s) => s.text === text) ? prev : [{ text, image }, ...prev]
            );
          }
        );
        if (!cancelled) setStarting(false);
      } catch (e) {
        if (cancelled) return;
        let msg = 'Unable to access the camera.';
        if (e && (e.name === 'NotAllowedError' || e.name === 'SecurityError')) {
          msg = 'Camera permission denied. Allow camera access to scan.';
        } else if (e && e.name === 'NotFoundError') {
          msg = 'No camera found on this device.';
        }
        setError(msg);
        setStarting(false);
      }
    };

    start();

    return () => {
      cancelled = true;
      stop();
    };
  }, [open, scanDelayMs, stop, captureFrame, playBeep, vibrate]);

  const handleClose = () => {
    stop();
    onClose();
  };

  if (!open) return null;

  return (
    <div
      onClick={handleClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: '10px', width: 'min(460px, 95vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>{title}</div>
            {subtitle ? (
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>{subtitle}</div>
            ) : null}
          </div>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', fontSize: isMobile ? '24px' : '18px', cursor: 'pointer', color: '#6b7280', lineHeight: 1, ...(isMobile ? { minWidth: '44px', minHeight: '44px' } : {}) }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 16px', overflowY: 'auto' }}>
          {error ? (
            <div style={{ fontSize: '12px', color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 12px' }}>
              {error}
            </div>
          ) : (
            <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', background: '#000', borderRadius: '8px', overflow: 'hidden' }}>
              <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {/* Framing guide */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ width: '70%', height: '45%', border: '2px solid rgba(255,255,255,0.85)', borderRadius: '8px', boxShadow: '0 0 0 2000px rgba(0,0,0,0.15)' }} />
              </div>
              {starting && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px' }}>
                  Starting camera…
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '10px', textAlign: 'center' }}>
            Point the camera at the QR code or barcode. Each scan is added automatically.
          </div>

          {/* Scanned reference list (image + serial). Reference only. */}
          {scans.length > 0 && (
            <div style={{ marginTop: '12px', borderTop: '1px dashed #e5e7eb', paddingTop: '12px' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>
                Scanned this session ({scans.length}):
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto' }}>
                {scans.map((s, idx) => (
                  <div key={s.text + idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 8px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                    {s.image ? (
                      <img src={s.image} alt="scan reference" style={{ width: '44px', height: '34px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, border: '1px solid #e5e7eb' }} />
                    ) : (
                      <div style={{ width: '44px', height: '34px', borderRadius: '4px', background: '#e5e7eb', flexShrink: 0 }} />
                    )}
                    <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#0369a1', fontWeight: '600', wordBreak: 'break-all' }}>{s.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <button
            onClick={handleClose}
            style={{ fontSize: '12px', padding: '6px 16px', background: '#0d6efd', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '600' }}
          >Done</button>
        </div>
      </div>
    </div>
  );
}
